/**
 * Mapping Memory Service
 * 
 * Service to remember and retrieve previous column mappings for CSV imports.
 * Uses local storage for persistence between sessions.
 */
export interface StoredMapping {
  id: string;
  importType: 'sales_data' | 'ad_spend_data';
  name: string;
  mapping: Record<string, string>;
  lastUsed: string;
  headers: string[];
}

const STORAGE_KEY = 'roi_csv_mappings';

class MappingMemoryService {
  // Retrieve all saved mappings
  getSavedMappings(importType: 'sales_data' | 'ad_spend_data'): StoredMapping[] {
    try {
      const storedData = localStorage.getItem(STORAGE_KEY);
      if (!storedData) return [];
      
      const allMappings: StoredMapping[] = JSON.parse(storedData);
      // Filter by import type and sort by most recently used
      return allMappings
        .filter(mapping => mapping.importType === importType)
        .sort((a, b) => new Date(b.lastUsed).getTime() - new Date(a.lastUsed).getTime());
    } catch (error) {
      console.error('Error retrieving saved mappings:', error);
      return [];
    }
  }

  // Save a new mapping
  saveMapping(
    importType: 'sales_data' | 'ad_spend_data',
    name: string,
    mapping: Record<string, string>,
    headers: string[]
  ): StoredMapping {
    try {
      // Get existing mappings
      const existingMappings = this.getAllMappings();
      
      // Create new mapping
      const newMapping: StoredMapping = {
        id: Date.now().toString(),
        importType,
        name,
        mapping,
        lastUsed: new Date().toISOString(),
        headers
      };
      
      // Add new mapping and save
      existingMappings.push(newMapping);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(existingMappings));
      
      return newMapping;
    } catch (error) {
      console.error('Error saving mapping:', error);
      throw error;
    }
  }

  // Update an existing mapping
  updateMapping(
    mappingId: string,
    updates: Partial<Omit<StoredMapping, 'id'>>
  ): StoredMapping | null {
    try {
      const allMappings = this.getAllMappings();
      const mappingIndex = allMappings.findIndex(m => m.id === mappingId);
      
      if (mappingIndex === -1) return null;
      
      // Update the mapping
      allMappings[mappingIndex] = {
        ...allMappings[mappingIndex],
        ...updates,
        lastUsed: new Date().toISOString()
      };
      
      // Save updates
      localStorage.setItem(STORAGE_KEY, JSON.stringify(allMappings));
      return allMappings[mappingIndex];
    } catch (error) {
      console.error('Error updating mapping:', error);
      return null;
    }
  }

  // Delete a mapping
  deleteMapping(mappingId: string): boolean {
    try {
      const allMappings = this.getAllMappings();
      const filteredMappings = allMappings.filter(m => m.id !== mappingId);
      
      if (filteredMappings.length === allMappings.length) {
        return false; // Nothing was deleted
      }
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredMappings));
      return true;
    } catch (error) {
      console.error('Error deleting mapping:', error);
      return false;
    }
  }

  // Find the best matching stored mapping for a set of headers
  findBestMatchingMapping(
    importType: 'sales_data' | 'ad_spend_data',
    headers: string[]
  ): StoredMapping | null {
    try {
      const mappings = this.getSavedMappings(importType);
      if (mappings.length === 0) return null;
      
      // Score each mapping based on header similarity
      const scoredMappings = mappings.map(mapping => {
        // Convert headers to lowercase for case-insensitive comparison
        const storedHeadersLower = mapping.headers.map(h => h.toLowerCase());
        const currentHeadersLower = headers.map(h => h.toLowerCase());
        
        // Calculate matching headers
        const matchingHeaders = storedHeadersLower.filter(h => 
          currentHeadersLower.includes(h)
        ).length;
        
        // Calculate match percentage 
        const matchPercentage = (matchingHeaders / headers.length) * 100;
        
        return {
          mapping,
          score: matchPercentage
        };
      });
      
      // Sort by score descending
      scoredMappings.sort((a, b) => b.score - a.score);
      
      // Return best match if it's above threshold (50% match)
      return scoredMappings[0]?.score >= 50 ? scoredMappings[0].mapping : null;
    } catch (error) {
      console.error('Error finding best matching mapping:', error);
      return null;
    }
  }

  // Get all mappings (private helper)
  private getAllMappings(): StoredMapping[] {
    try {
      const storedData = localStorage.getItem(STORAGE_KEY);
      return storedData ? JSON.parse(storedData) : [];
    } catch (error) {
      console.error('Error parsing stored mappings:', error);
      return [];
    }
  }
}

export default new MappingMemoryService();